import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, SearchLg, Edit05, Trash01, Eye } from "@untitledui/icons";
import { useAuth } from "@/context/AuthProvider";
import PageLayout from "@/components/layout/PageLayout";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Dialog, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import CustomerForm from "./CustomerForm";
import {
  getCustomers,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  type CreateCustomerPayload,
} from "@/api/customers";
import type { CustomerDto, PaginatedResponse } from "@/types/dto";

const CAN_CREATE = ["admin", "owner", "manager", "receptionist"];
const CAN_EDIT = ["admin", "owner", "manager"];
const CAN_DELETE = ["admin", "owner"];

export default function CustomersListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [result, setResult] = useState<PaginatedResponse<CustomerDto> | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // Dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<CustomerDto | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const canCreate = CAN_CREATE.includes(user?.role ?? "");
  const canEdit = CAN_EDIT.includes(user?.role ?? "");
  const canDelete = CAN_DELETE.includes(user?.role ?? "");

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCustomers({ page, limit: 20, search: search || undefined });
      setResult(data);
    } catch {
      // handled by interceptor
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Debounced search
  useEffect(() => {
    setPage(1);
  }, [search]);

  const handleCreate = async (payload: CreateCustomerPayload) => {
    setSaving(true);
    try {
      await createCustomer(payload);
      setFormOpen(false);
      fetchCustomers();
      toast.success("Customer created");
    } catch {
      toast.error("Failed to create customer");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (payload: CreateCustomerPayload) => {
    if (!editingCustomer) return;
    setSaving(true);
    try {
      await updateCustomer(editingCustomer.id, payload);
      setEditingCustomer(null);
      setFormOpen(false);
      fetchCustomers();
      toast.success("Customer updated");
    } catch {
      toast.error("Failed to update customer");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirmId) return;
    setSaving(true);
    try {
      await deleteCustomer(deleteConfirmId);
      setDeleteConfirmId(null);
      fetchCustomers();
      toast.success("Customer deleted");
    } catch {
      toast.error("Failed to delete customer");
    } finally {
      setSaving(false);
    }
  };

  const customers = result?.data ?? [];
  const pagination = result?.pagination;

  return (
    <PageLayout
      title="Customers"
      subtitle="Manage your customer directory"
      actions={
        canCreate ? (
          <Button
            size="md"
            color="primary"
            iconLeading={Plus}
            onClick={() => {
              setEditingCustomer(null);
              setFormOpen(true);
            }}
          >
            Add Customer
          </Button>
        ) : undefined
      }
    >
      {/* Search */}
      <div className="mb-4 sm:mb-6 max-w-sm">
        <Input
          placeholder="Search customers..."
          icon={SearchLg}
          value={search}
          onChange={setSearch}
        />
      </div>

      {/* Empty / Loading state */}
      {loading && customers.length === 0 ? (
        <div className="py-12 text-center text-text-tertiary">Loading...</div>
      ) : customers.length === 0 ? (
        <div className="py-12 text-center text-text-tertiary">
          {search ? "No customers match your search." : "No customers yet."}
        </div>
      ) : (
        <>
          {/* Mobile Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 xl:hidden">
            {customers.map((c) => (
              <div
                key={c.id}
                className="rounded-xl border border-border-secondary bg-bg-primary p-4"
              >
                <div className="flex items-start justify-between">
                  <div
                    className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => navigate(`/customers/${c.id}`)}
                  >
                    <p className="font-medium text-text-primary">
                      {c.firstName} {c.lastName}
                    </p>
                    {c.email && (
                      <p className="text-sm text-text-secondary mt-0.5 truncate">{c.email}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0 ml-2">
                    {canEdit && (
                      <Button
                        size="sm"
                        color="link-gray"
                        iconLeading={Edit05}
                        onClick={() => {
                          setEditingCustomer(c);
                          setFormOpen(true);
                        }}
                        aria-label="Edit"
                      />
                    )}
                    {canDelete && (
                      <Button
                        size="sm"
                        color="link-destructive"
                        iconLeading={Trash01}
                        onClick={() => setDeleteConfirmId(c.id)}
                        aria-label="Delete"
                      />
                    )}
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-text-tertiary">
                  {c.phone && <span>{c.phone}</span>}
                  {c.address?.city && <span>{c.address.city}</span>}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table */}
          <div className="hidden xl:block overflow-x-auto rounded-xl border border-border-secondary">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-secondary bg-bg-secondary">
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Name</th>
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Email</th>
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Phone</th>
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">City</th>
                  <th className="px-6 py-3 text-right font-medium text-text-tertiary">Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr
                    key={c.id}
                    className="border-b border-border-secondary last:border-0 hover:bg-bg-secondary_hover transition-colors"
                  >
                    <td className="px-6 py-4 font-medium text-text-primary">
                      {c.firstName} {c.lastName}
                    </td>
                    <td className="px-6 py-4 text-text-secondary">{c.email ?? "—"}</td>
                    <td className="px-6 py-4 text-text-secondary">{c.phone ?? "—"}</td>
                    <td className="px-6 py-4 text-text-secondary">{c.address?.city ?? "—"}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          color="link-gray"
                          iconLeading={Eye}
                          onClick={() => navigate(`/customers/${c.id}`)}
                          aria-label="View"
                        />
                        {canEdit && (
                          <Button
                            size="sm"
                            color="link-gray"
                            iconLeading={Edit05}
                            onClick={() => {
                              setEditingCustomer(c);
                              setFormOpen(true);
                            }}
                            aria-label="Edit"
                          />
                        )}
                        {canDelete && (
                          <Button
                            size="sm"
                            color="link-destructive"
                            iconLeading={Trash01}
                            onClick={() => setDeleteConfirmId(c.id)}
                            aria-label="Delete"
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-text-tertiary">
            Page {pagination.page} of {pagination.totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              color="secondary"
              isDisabled={pagination.page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Button
              size="sm"
              color="secondary"
              isDisabled={pagination.page >= pagination.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setEditingCustomer(null);
        }}
      >
        <DialogTitle>{editingCustomer ? "Edit Customer" : "Add Customer"}</DialogTitle>
        <DialogDescription>
          {editingCustomer
            ? "Update the customer's information below."
            : "Fill in the details to add a new customer."}
        </DialogDescription>
        <CustomerForm
          key={editingCustomer?.id ?? "new"}
          defaultValues={editingCustomer ?? undefined}
          onSubmit={editingCustomer ? handleEdit : handleCreate}
          onCancel={() => {
            setFormOpen(false);
            setEditingCustomer(null);
          }}
          isLoading={saving}
        />
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteConfirmId}
        onOpenChange={(open) => {
          if (!open) setDeleteConfirmId(null);
        }}
      >
        <DialogTitle>Delete Customer</DialogTitle>
        <DialogDescription>
          Are you sure you want to delete this customer? This action cannot be undone.
        </DialogDescription>
        <div className="flex justify-end gap-3">
          <Button
            size="md"
            color="secondary"
            onClick={() => setDeleteConfirmId(null)}
          >
            Cancel
          </Button>
          <Button
            size="md"
            color="primary-destructive"
            isLoading={saving}
            isDisabled={saving}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </div>
      </Dialog>
    </PageLayout>
  );
}
